<template>
  <v-container fluid v-if="routeLoaded">
    <customer-context-prompt :resource="route"/>
    <dialog-confirm ref="generic-dialog-confirm">
      <template #title>
        {{ genericDialogTitle }}
      </template>
      <div>
        {{ genericDialogContent }}
      </div>
    </dialog-confirm>
    <div class="d-flex justify-space-between align-center">
      <div class="d-flex align-center">
        <h2 class="mr-2">
          <span v-if="route.route_plan_id">{{ $t('edit_route') }} {{ route.name }}</span>
          <span v-else>{{ $t('edit') }} {{ route.name }}</span>
        </h2>
        <v-chip v-if="route.deleted" dark small label class="ml-2">
          {{ $t('deleted') }}
        </v-chip>
        <v-chip v-if="route.archived" dark small label class="ml-2">
          {{ $t('archived') }}
        </v-chip>
      </div>
      <delete-route-dialog v-if="route.deleted == null" ref="delete-route-dialog" :route="route">
        <template #activator="{ on, attrs }">
          <v-btn outlined color="danger" dark v-bind="attrs" v-on="on">
            <v-icon class="mr-1 ml-n1">mdi-delete</v-icon>{{ $t('delete_route') }}
          </v-btn>
        </template>
      </delete-route-dialog>
      <div v-else-if="user.can('restore_route')">
        <v-btn
          outlined
          v-if="route.deleted || isRestoring"
          @click="restoreRoute" color="primary"
          :loading="isRestoring"
          :disabled="route.archived">
          <v-icon class="mr-1 ml-n1">mdi-redo</v-icon>{{ $tc('restore_route' )}}
        </v-btn>
      </div>
    </div>
    <route-form
      ref="route-form"
      :route="route"
      v-on:updated="routeUpdated($event)"/>
    <v-expansion-panels v-if="user.can('view_auditlog')" class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="text-subtitle-1">{{ $tc('audit_log', 2) }}</div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <audit-log
            ref="audit-log"
            autoload
            :table-controls="['actor', 'date_range']"
            :table-columns="['user', 'customer', 'action', 'timestamp']"
            :initSearchCriteria="auditLogSearchCriteria"/>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
  <v-container fluid v-else>
    <h2>{{ $t('edit_route') }}</h2>
    <v-card class="mt-3">
      <v-card-text>
        <v-row>
          <v-col>
            <v-skeleton-loader type="image" height="30"/>
            <v-skeleton-loader type="image" height="30" class="mt-8"/>
          </v-col>
          <v-col>
            <v-skeleton-loader type="image"/>
            <v-skeleton-loader type="image"/>
          </v-col>
          <v-col>
            <v-skeleton-loader type="card"/>
          </v-col>
          <v-col>
            <v-skeleton-loader type="card"/>
          </v-col>
        </v-row>
        <div class="d-flex justify-space-between mt-4">
          <v-skeleton-loader type="button"/>
          <v-skeleton-loader type="button"/>
        </div>
      </v-card-text>
    </v-card>
    <v-expansion-panels v-if="user.can('view_auditlog')" class="mt-4" disabled>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <div class="text-subtitle-1">{{ $tc('audit_log', 2) }}</div>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-container>
</template>

<script>
import RouteForm from '@/components/route/form/RouteForm'
import DeleteRouteDialog from '@/components/route/form/DeleteRouteDialog'
import DialogConfirm from '@/components/shared/DialogConfirm'
import AuditLog from '@/components/audit-log/AuditLog'
import CustomerContextPrompt from '@/components/shared/CustomerContextPrompt'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'

export default {
  name: 'RouteUpdate',
  components: {
    RouteForm,
    DeleteRouteDialog,
    DialogConfirm,
    AuditLog,
    CustomerContextPrompt
  },
  mixins: [
    CurrentUserMixin
  ],
  data () {
    return {
      route: {},
      routeLoaded: false,
      onUpdateRedirectTo: null,
      isRestoring: false,
      genericDialogTitle: null,
      genericDialogContent: null
    }
  },
  methods: {
    async fetchModel () {
      try {
        this.route = await this.$orm('Route').find(this.$route.params.id, {
          params: {
            expand: 'customer.customer_settings,vehicle,route_products,route_products.product,route_plan'
          }
        })
        this.routeLoaded = true
      } catch (error) {
        this.handleError(error)
      }
    },
    refreshAuditLog () {
      this.$refs['audit-log'].refreshResults()
    },
    /**
     * Redirects the user to the appropriate page once the route has been
     * updated
     *
     * @param {RouteRecord} route
     * @return undefined
     */
    routeUpdated (route) {
      this.$router.push(this.onUpdateRedirectTo || `/route/${route.id}`, () => {
        this.$store.dispatch(
          'globalMessagingModule/showSuccessFlashBanner',
          `${this.$i18n.tc('route')} ${route.name} ${this.$i18n.t('generic_save_success')}`
        )
      })
    },
    /**
     * Wrapper to the generic dialog so we can reuse it in a few contexts.
     * @param title
     * @param content
     * @returns {Promise<*>}
     */
    genericDialogConfirm (title, content) {
      this.genericDialogTitle = title
      this.genericDialogContent = content
      return this.$refs['generic-dialog-confirm'].confirm()
    },
    /**
     * Restores the route. If the route turns out to be in-progress after restoration, then ask the user if they want
     * to reprocess the route via a modal.
     */
    async restoreRoute () {
      if (await this.genericDialogConfirm(this.$t('are_you_sure'), this.$t('are_you_sure_you_want_to_restore_this'))) {
        this.$gtag.event('restore', { event_category: 'Route' })
        this.isRestoring = true
        try {
          const updateObj = {
            deleted: null
          }
          // If the deleted time is the same as the end time, the route was in-progress when it was deleted,
          // so reset the end time to null to mark it back as in progress.
          if (this.route.end === this.route.deleted) {
            updateObj.end = null
          }
          this.route = await this.$orm('Route').update(
            this.route.id,
            updateObj,
            {
              params: {
                expand: 'customer.customer_settings,vehicle,route_products,route_products.product,route_plan'
              }
            }
          )
          if (this.route.end == null) {
            const modalMessage = this.$t('route_reprocessing_restoration_processing_message')
            if (await this.genericDialogConfirm(this.$t('reprocess_route'), modalMessage)) {
              try {
                await this.$httpAdapter({
                  url: `${this.$orm('Route').endpoint}/${this.route.id}/reprocess-data`,
                  method: 'GET'
                })
              } catch (error) {
                this.handleError(error)
              }
            }
          }
          await this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner', this.$tc('route_restore_successful')
          )
        } catch (error) {
          this.handleError(error)
        } finally {
          this.isRestoring = false
        }
      }
    }
  },
  computed: {
    auditLogSearchCriteria () {
      return {
        resourceType: 'route',
        primaryKey: this.route.id,
        createdAfter: this.route.created
      }
    }
  },
  created () {
    this.fetchModel()
  },

  /**
   * Catch where we came from, so we know where to redirect back to. See
   * https://router.vuejs.org/guide/advanced/navigation-guards.html for more details.
   */
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (from.path !== '/') {
        vm.onUpdateRedirectTo = from.path
      }
    })
  }
}
</script>

<style scoped lang="scss">
</style>
